import React, { useEffect, useState, MouseEvent } from 'react';
import { Box, Divider, Menu, MenuItem, Typography } from '@mui/material';
import HFAvatar from '@root/app/shared/new-components/hf-avatar/hf-avatar.component';
import HfUserProfileSettings from '@root/app/shared/new-components/hf-user-profile-settings/hfUserProfileSettings';
import { errorService } from '@root/app/shared/new-services/newError.service';
import { authService } from '@root/app/shared/new-services/auth.service';
import { navigateToState } from '@root/app/shared/navigation/navigation.service';
import { useTranslation } from '@root/app/shared/hooks/useTranslation';

interface AuthUser {
    name?: string;
    role?: string;
}

const HfUserProfileMenu: React.FC = () => {
    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [user, setUser] = useState<AuthUser | null>(null);
    const [userImage, setUserImage] = useState<string | null>(null);
    const [showProfileSettings, setShowProfileSettings] = useState(false);

    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const logout = () => {
        authService.logout(
            () => {
                navigateToState('onboarding');
                window.location.reload();
            },
            (err) => {
                navigateToState('onboarding');
                errorService.showErrorModal(err);
            }
        );
    };

    useEffect(() => {
        setUser(authService.user());
    }, []);

    useEffect(() => {
        setUserImage(authService.getUserImage());
    }, [user]);

    useEffect(() => {
        setUser(authService.user());
    }, [showProfileSettings]);

    return (
        <div>
            <Box
                sx={theme => ({
                    padding: theme.spacing(1),
                    cursor: 'pointer'
                })}
                id='profile-dropdown-menu'
                aria-controls={open ? 'profile-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
            >
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <HFAvatar
                        name={user?.name}
                        image={userImage}
                        size='small'
                    />
                    <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant='h6' sx={theme => ({ marginLeft: theme.spacing(1), whiteSpace: 'nowrap' })}>
                            {user?.name}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Typography
                                variant='caption'
                                color='text.secondary'
                                sx={{
                                    display: { xs: 'none', sm: 'block' },
                                    textTransform: 'capitalize'
                                }}
                            >
                                {user?.role ? t(user.role) : ''}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Menu
                id='profile-menu'
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'profile-dropdown-menu'
                }}
            >
                <MenuItem onClick={() => { setShowProfileSettings(true); }}>{t('MY_PROFILE')}</MenuItem>
                <Divider />
                <MenuItem onClick={logout} sx={{ color: 'error.main' }}>{t('LOG_OUT')}</MenuItem>
            </Menu>
            <HfUserProfileSettings
                show={showProfileSettings}
                onClose={() => { setShowProfileSettings(false); }}
            />
        </div>
    );
};

export default HfUserProfileMenu;
